import { useCallback, useState } from "react";
import { Upload, FileUp, Loader2, AlertCircle, Download } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/marketing/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/marketing/components/ui/table";
import { Badge } from "@/marketing/components/ui/badge";
import { parseCsv, type ParsedInfluencer } from "@/marketing/lib/csvParser";
import { influencerAPIClient } from "@/marketing/infrastructure/influencer/influencerAPIClient";
import { INFLUENCER_KEYS } from "@/marketing/infrastructure/influencer/utils/keys";

type Step = "upload" | "preview" | "result";

interface ImportResult {
  created: number;
  skipped: number;
  errors: string[];
}

export function ImportDialog() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("upload");
  const [parsedData, setParsedData] = useState<ParsedInfluencer[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const queryClient = useQueryClient();

  const downloadTemplate = () => {
    const header = "Handle,Platform,Display Name,Email,Country";
    const example =
      "@example_user,tiktok,Example Creator,creator@example.com,US";
    const blob = new Blob([`${header}\n${example}\n`], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "influencer_import_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setStep("upload");
    setParsedData([]);
    setParseErrors([]);
    setResult(null);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) reset();
  };

  const processFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result;
      if (typeof content !== "string") {
        toast.error("Failed to read file content");
        return;
      }
      const { data, errors } = parseCsv(content);
      setParsedData(data);
      setParseErrors(errors);
      setStep("preview");
    };
    reader.onerror = () => {
      toast.error("Failed to read file");
    };
    reader.readAsText(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith(".csv")) {
      processFile(file);
    } else {
      toast.error("Please drop a .csv file");
    }
  }, []);

  const handleImport = async () => {
    if (parsedData.length === 0) return;
    setIsImporting(true);
    try {
      const res = await influencerAPIClient.bulkCreateInfluencers(parsedData);
      setResult(res);
      setStep("result");
      queryClient.invalidateQueries({ queryKey: INFLUENCER_KEYS.all });
      toast.success(`Imported ${res.created} influencer(s)`);
    } catch (err) {
      console.error("Import failed:", err);
      toast.error("Failed to import influencers");
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-foreground hover:tw-bg-accent">
          <Upload className="tw-h-3.5 tw-w-3.5" />
          Import CSV
        </button>
      </DialogTrigger>
      <DialogContent className="sm:tw-max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import Influencers from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV file with influencer data. Supported columns: handle,
            platform, name, email, region, social media URL.
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div
            className={`tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-lg tw-border-2 tw-border-dashed tw-p-12 tw-transition-colors ${
              isDragOver ? "tw-border-blue-500 tw-bg-blue-50" : "tw-border-muted"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
          >
            <FileUp className="tw-mb-3 tw-h-10 tw-w-10 tw-text-muted-foreground" />
            <p className="tw-mb-1 tw-text-sm tw-font-medium">
              Drop a CSV file here, or click to browse
            </p>
            <p className="tw-mb-4 tw-text-xs tw-text-muted-foreground">
              Supports .csv files
            </p>
            <div className="tw-flex tw-items-center tw-gap-3">
              <label className="tw-cursor-pointer tw-rounded-md tw-bg-[#2563EB] tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-blue-700">
                Choose File
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="tw-hidden"
                />
              </label>
              <button
                type="button"
                onClick={downloadTemplate}
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-muted-foreground hover:tw-text-foreground"
              >
                <Download className="tw-h-3.5 tw-w-3.5" />
                Download Template
              </button>
            </div>
          </div>
        )}

        {step === "preview" && (
          <div className="tw-space-y-4">
            <div className="tw-flex tw-items-center tw-gap-2">
              <Badge variant="secondary">{parsedData.length} rows parsed</Badge>
              {parseErrors.length > 0 && (
                <Badge variant="destructive">{parseErrors.length} errors</Badge>
              )}
            </div>

            {parseErrors.length > 0 && (
              <div className="tw-rounded-md tw-border tw-border-red-200 tw-bg-red-50 tw-p-3">
                <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-red-800">
                  <AlertCircle className="tw-h-4 tw-w-4" />
                  Parse Warnings
                </div>
                <ul className="tw-mt-1 tw-list-inside tw-list-disc tw-text-xs tw-text-red-700">
                  {parseErrors.slice(0, 5).map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                  {parseErrors.length > 5 && (
                    <li>...and {parseErrors.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}

            {parsedData.length > 0 && (
              <div className="tw-max-h-64 tw-overflow-auto tw-rounded-md tw-border">
                <Table>
                  <TableHeader>
                    <TableRow className="tw-bg-neutral-50">
                      <TableHead>Handle</TableHead>
                      <TableHead>Platform</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Region</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsedData.slice(0, 10).map((row, i) => (
                      <TableRow key={i}>
                        <TableCell className="tw-text-sm">@{row.handle}</TableCell>
                        <TableCell className="tw-text-sm">
                          {row.platform}
                        </TableCell>
                        <TableCell className="tw-text-sm">
                          {row.displayName ?? "-"}
                        </TableCell>
                        <TableCell className="tw-text-sm">
                          {row.emails?.[0] ?? "-"}
                        </TableCell>
                        <TableCell className="tw-text-sm">
                          {row.countrySignals?.[0] ?? "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {parsedData.length > 10 && (
                  <p className="tw-px-3 tw-py-2 tw-text-xs tw-text-muted-foreground">
                    Showing 10 of {parsedData.length} rows
                  </p>
                )}
              </div>
            )}

            <div className="tw-flex tw-justify-end tw-gap-2">
              <button
                onClick={reset}
                className="tw-rounded-md tw-border tw-border-input tw-px-4 tw-py-2 tw-text-sm tw-font-medium hover:tw-bg-accent"
              >
                Back
              </button>
              <button
                onClick={handleImport}
                disabled={isImporting || parsedData.length === 0}
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-bg-[#2563EB] tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-blue-700 disabled:tw-opacity-50"
              >
                {isImporting && <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin" />}
                Import {parsedData.length} Influencer(s)
              </button>
            </div>
          </div>
        )}

        {step === "result" && result && (
          <div className="tw-space-y-4">
            <div className="tw-rounded-md tw-border tw-border-green-200 tw-bg-green-50 tw-p-4">
              <p className="tw-text-sm tw-font-medium tw-text-green-800">
                Import Complete
              </p>
              <div className="tw-mt-2 tw-space-y-1 tw-text-sm tw-text-green-700">
                <p>Created: {result.created}</p>
                <p>Skipped (duplicates): {result.skipped}</p>
                {result.errors.length > 0 && (
                  <p className="tw-text-red-600">Errors: {result.errors.length}</p>
                )}
              </div>
            </div>
            <div className="tw-flex tw-justify-end">
              <button
                onClick={() => handleOpenChange(false)}
                className="tw-rounded-md tw-bg-[#2563EB] tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
